import cx from 'classnames';
import { findIndex, kebabCase } from 'lodash';
import React, { FC, ReactNode, useState } from 'react';
import { Trans } from '@lingui/macro';
import { PropsWithClassName } from '@/types/index';
import useLocalAnchorNavigate from '@/hooks/useLocalAnchorNavigate';
import * as styles from './FAQs.module.scss';

type FAQ = {
  title: string;
  children?: ReactNode;
  slug?: string;
};

type Props = PropsWithClassName<{
  items: FAQ[];
  title?: ReactNode;
}>;

const findActiveIndexFromHash = (items: FAQ[]) => {
  if (typeof window !== 'undefined') {
    const hash = window.location.hash.slice(1);
    const index = findIndex(items, ({ title, slug = kebabCase(title) }) => slug === hash);
    return index !== -1 ? index : undefined;
  }
};

const findRealIndex = (items: FAQ[], item: FAQ) =>
  findIndex(items, ({ title }) => item.title === title);

const FAQs: FC<Props> = ({ className, items, title }) => {
  const [activeIndex, setActiveIndex] = useState(findActiveIndexFromHash(items));
  const { navigate } = useLocalAnchorNavigate();
  const handleFAQToggle = (index: number, slug: string) => {
    const nextIndex = activeIndex === index ? undefined : index;
    setActiveIndex(nextIndex);
    navigate(`#${slug}`);
  };

  return (
    <div className={cx(styles.main, className)}>
      <div className={styles.title}>{title}</div>
      <div className={styles.answers}>
        <p className={styles.intro}>
          <Trans>
            Your questions, our solutions. Navigate through our FAQs for instant insights
            and answers.
          </Trans>
        </p>
        {items.map(({ title, children, slug = kebabCase(title) }, rowIndex) => (
          <div
            key={rowIndex}
            className={styles.item}
            aria-expanded={findRealIndex(items, { title }) === activeIndex}
            id={slug}
          >
            <p
              aria-controls={kebabCase(title)}
              onClick={() => handleFAQToggle(findRealIndex(items, { title }), slug)}
              role="button"
              className={styles.title}
            >
              {title}
              <span className={styles.icon}>
                {findRealIndex(items, { title }) === activeIndex ? '-' : '+'}
              </span>
            </p>
            {/**
             * @todo: Add animation for the body
             */}
            <div className={styles.body}>{children}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQs;
